import qs from 'qs'

import { deleteRequest, getRequest, patchRequest, postFormFileRequest, postRequest } from '../../../../utils'
import { FecAccountMatchType } from './enums/fecAccountMatchType'
import { FecImportFileType } from './enums/fecImportFileType'
import { FecImportLifecycleStatus } from './enums/fecImportLifecycleStatus'
import { FecImportProcessingStatus } from './enums/fecImportProcessingStatus'
import { FecJournalType } from './enums/fecJournalType'
import { FecMappingDetectionStatus } from './enums/fecMappingDetectionStatus'
import { FecMappingReviewStatus } from './enums/fecMappingReviewStatus'
import { FecPushStatus } from './enums/fecPushStatus'
import { FecThirdPartyCategory } from './enums/fecThirdPartyCategory'
import { FecThirdPartyRole } from './enums/fecThirdPartyRole'

export interface FecImportValidationError {
  column?: string | null
  line?: number | null
  message: string
}

/**
 * An FEC import as returned by the API (ADVP-585): list items, detail, `POST`, `PATCH` and `POST :id/retry` share it.
 * Dates are ISO strings. The per-domain fields are `null` until the import has a mapping session.
 */
export interface FecImportItem {
  accountDetectionStatus: FecMappingDetectionStatus | null
  accountMappingStatus: FecMappingReviewStatus | null
  accountPushStatus: FecPushStatus | null
  accountingResult: string | null
  /** Retry is available (`parsing_failed`, `detection_failed`); `push_failed` is retried by re-confirming the domain. */
  canRetry: boolean
  /** Deleting is allowed until a domain is confirmed, including while parsing or detecting. */
  canDelete: boolean
  createdTime: string
  detectedEditor: string | null
  entryPushStatus: FecPushStatus | null
  errorMessage: string | null
  errors: FecImportValidationError[] | null
  fileName: string
  fileType: FecImportFileType | null
  /** A correction has not reached Billy yet; the status stays `done`. */
  hasPendingCorrections: boolean
  id: string
  journalDetectionStatus: FecMappingDetectionStatus | null
  journalMappingStatus: FecMappingReviewStatus | null
  journalPushStatus: FecPushStatus | null
  lineCount: number | null
  /** Detection/recovery error text; only meaningful while `status` is `detection_failed`. */
  mappingErrorMessage: string | null
  organizationId: string
  periodEnd: string | null
  periodStart: string | null
  processingStatus: FecImportProcessingStatus
  /** Lifecycle from upload to "pushed to Billy": what to show, filter and resume on. */
  status: FecImportLifecycleStatus
  thirdPartyDetectionStatus: FecMappingDetectionStatus | null
  thirdPartyMappingStatus: FecMappingReviewStatus | null
  thirdPartyPushStatus: FecPushStatus | null
  updatedTime: string
  uploadedByUserId: string
}

export interface FecImportListResponse {
  items: FecImportItem[]
  /** Matching imports before `limit`/`offset`. */
  total: number
}

export interface FecImportListParams {
  fileType?: FecImportFileType
  /** Default 50, max 200. */
  limit?: number
  offset?: number
  /** Only imports whose lifecycle `status` is not `done` (failed ones included). */
  open?: boolean
  status?: FecImportLifecycleStatus[]
}

export type UploadedFecImport = FecImportItem

export const uploadFecFile = (organizationId: string, file: File, signal?: AbortSignal): Promise<UploadedFecImport[]> =>
  postFormFileRequest({
    endpoint: `/organizations/${organizationId}/fec-imports/`,
    fields: {},
    file,
    signal,
  })

export const fetchFecImportList = (
  organizationId: string,
  params: FecImportListParams = {},
): Promise<FecImportListResponse> => {
  const queryParams = qs.stringify(params, { arrayFormat: 'repeat' })
  const queryString = queryParams ? `?${queryParams}` : ''

  return getRequest(`/organizations/${organizationId}/fec-imports/${queryString}`)
}

/** Re-runs a failed parse or detection. The import comes back in its new state, normally `detecting_mappings`. */
export const retryFecImport = (organizationId: string, fecImportId: string): Promise<FecImportItem> =>
  postRequest(`/organizations/${organizationId}/fec-imports/${fecImportId}/retry`, {})

export const deleteFecImport = (organizationId: string, fecImportId: string): Promise<void> =>
  deleteRequest(`/organizations/${organizationId}/fec-imports/${fecImportId}`)

export interface FecJournalMapping {
  cpaStatus: string
  detectedType: FecJournalType
  exclusionReason: string | null
  hasSiblings: boolean
  id: string
  isExcluded: boolean
  journalCode: string
  journalLib: string
  linkedBankAccountId: string | null
  mappedDaybookId: string | null
  mappedJournalCode: string | null
  siblingIds: string[]
}

export const fetchFecJournalMappings = (organizationId: string, fecImportId: string): Promise<FecJournalMapping[]> =>
  getRequest(`/organizations/${organizationId}/fec-imports/${fecImportId}/journal-mappings/`)

export interface UpdateFecJournalMappingPayload {
  detectedType?: FecJournalType
  isExcluded?: boolean
  linkedBankAccountId?: string | null
}

export const updateFecJournalMapping = (
  organizationId: string,
  fecImportId: string,
  mappingId: string,
  payload: UpdateFecJournalMappingPayload,
): Promise<FecJournalMapping> =>
  patchRequest(`/organizations/${organizationId}/fec-imports/${fecImportId}/journal-mappings/${mappingId}`, payload)

export const confirmFecJournalMappings = (organizationId: string, fecImportId: string): Promise<void> =>
  postRequest(`/organizations/${organizationId}/fec-imports/${fecImportId}/journal-mappings/confirm`, {})

export interface FecAccountMappingItem {
  cpaStatus: string
  customLabel: string | null
  fecAccountNo: string
  fecLabel: string
  id: string
  matchType: FecAccountMatchType
  possibleDuplicateAccountId: string | null
  possibleDuplicateAccountName: string | null
  possibleDuplicateReason: string | null
  predefinedAccountId: string | null
}

export interface FecAccountMappingGroup {
  existsInBilly?: boolean
  parentAccountNo: string
  parentLabel: string
  subAccounts: FecAccountMappingItem[]
}

export interface FecAccountMappingsResponse {
  groups: FecAccountMappingGroup[]
  unknown: FecAccountMappingItem[]
}

export const fetchFecAccountMappings = (
  organizationId: string,
  fecImportId: string,
): Promise<FecAccountMappingsResponse> =>
  getRequest(`/organizations/${organizationId}/fec-imports/${fecImportId}/account-mappings/`)

export const confirmFecAccountMappings = (organizationId: string, fecImportId: string): Promise<void> =>
  postRequest(`/organizations/${organizationId}/fec-imports/${fecImportId}/account-mappings/confirm`, {})

interface FecThirdPartyMappingItemBase {
  auxAccountNo: string
  /** The FEC name; for several names on one account number the backend returns the most recent. */
  auxLabel: string
  detectedType: FecThirdPartyRole
  id: string
}

/** The number is free in this role, so there is no Billy contact to match. */
export interface FecThirdPartyNewMappingItem extends FecThirdPartyMappingItemBase {
  billyAccountNumber: null
  billyContactId: null
  billyContactName: null
  category: FecThirdPartyCategory.Create
}

/** The number exists in this role: the `billy*` fields describe the contact that owns it. */
export interface FecThirdPartyExistingMappingItem extends FecThirdPartyMappingItemBase {
  billyAccountNumber: string
  billyContactId: string
  billyContactName: string
  category: FecThirdPartyCategory.Blocked | FecThirdPartyCategory.Reuse | FecThirdPartyCategory.SimilarName
}

/**
 * One FEC third party, classified against the Billy contacts by role and FEC auxiliary account number (ADVP-629).
 * Matching never uses the name.
 */
export type FecThirdPartyMappingItem = FecThirdPartyExistingMappingItem | FecThirdPartyNewMappingItem

/** Counts of the rows of one import, by category. Not deduped across imports, so not used for the tab badge. */
export interface FecThirdPartyMappingCounts {
  blocked: number
  /** Categories `reuse` and `similarName`. */
  existing: number
  /** Category `create`. */
  new: number
  similarName: number
  total: number
}

/** All rows come in one response, with the counts computed from them. */
export interface FecThirdPartyMappingsResponse extends FecThirdPartyMappingCounts {
  /** The organisation has third-party contacts with an account number in Billy; drives the status tag. */
  hasExistingContacts: boolean
  items: FecThirdPartyMappingItem[]
}

export const fetchFecThirdPartyMappings = (
  organizationId: string,
  fecImportId: string,
): Promise<FecThirdPartyMappingsResponse> =>
  getRequest(`/organizations/${organizationId}/fec-imports/${fecImportId}/thirdparty-mappings/`)

export const confirmFecThirdPartyMappings = (organizationId: string, fecImportId: string): Promise<void> =>
  postRequest(`/organizations/${organizationId}/fec-imports/${fecImportId}/thirdparty-mappings/confirm`, {})
